import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Icon,
  Paper,
  Box,
  Card,
} from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

// --- FAKE DATA FOR DEMONSTRATION ---
const FAKE_DECOMPOSITION_DATA = [
  {
    productFulfillmentDecompRules: [
      {
        recordId: '13UHr000000PD4yMAG',
        fields: { Priority: 2, Name: 'POS Bundle - Billing' },
        sourceProductName: 'Retail POS Enterprise',
        destinationProductName: 'Billing Service',
      },
      {
        recordId: '13UHr000000PD55MAG',
        fields: { Priority: 1, Name: 'Ecommerce Billing' },
        sourceProductName: 'E-Commerce',
        destinationProductName: 'Billing Service',
      },
    ],
    valueTransformations: [
      {
        recordId: '1B7Hr000000sYh9KAE',
        fields: { OutputNumber: 4.0, InputNumber: 3.0, Name: '000000013' },
        valueTransformationGroupName: 'Billing',
      },
      {
        recordId: '1B7Hr000000sYgzKAE',
        fields: { OutputNumber: 4.0, InputNumber: 1.0, Name: '000000011' },
        valueTransformationGroupName: 'Billing',
      },
    ],
  },
];
// --- END OF FAKE DATA ---

const ValueTransformationsTable = ({ rules, fieldsToShow = [], customRender = {} }) => {
  const headers =
    fieldsToShow.length > 0 ? fieldsToShow : [...Object.keys(rules[0].fields), ...Object.keys(customRender)];

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead sx={{ display: 'contents' }}>
          <TableRow>
            {headers.map((field) => (
              <TableCell key={field}>
                <ArgonTypography variant="caption" fontWeight="bold">
                  {field}
                </ArgonTypography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rules.map((rule) => (
            <TableRow key={rule.recordId}>
              {headers.map((field) => (
                <TableCell key={`${rule.recordId}-${field}`}>
                  {customRender[field] ? customRender[field](rule) : rule.fields[field] ?? 'N/A'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

function DecompositionTab({ product, fieldsConfig = {} }) {
  const [expandedRule, setExpandedRule] = useState({});

  const orchestrators = product?.related_data?.dynamicRevenueOrchestrators || FAKE_DECOMPOSITION_DATA;

  if (!orchestrators || orchestrators.length === 0) {
    return (
      <ArgonBox p={3}>
        <ArgonTypography variant="body2">No Decomposition data available for this product.</ArgonTypography>
      </ArgonBox>
    );
  }

  const handleToggle = (key) => {
    setExpandedRule((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <ArgonBox p={2}>
      {orchestrators.map((orchestrator, orchestratorIndex) => (
        <Box key={orchestrator.productId || orchestratorIndex}>
          {orchestrator.productFulfillmentDecompRules.map((rule) => {
            const ruleId = rule.recordId;
            const hasTransformations =
              orchestrator.valueTransformations && orchestrator.valueTransformations.length > 0;

            return (
              <Card key={ruleId} sx={{ mb: 2 }}>
                <ArgonBox p={1} mb={0.5} mt={0.5} onClick={() => handleToggle(ruleId)} sx={{ cursor: 'pointer' }}>
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                    <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                      {rule.fields.Name}
                    </ArgonTypography>
                    <Icon>{expandedRule[ruleId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                  </ArgonBox>
                </ArgonBox>
                <Collapse in={expandedRule[ruleId]} timeout="auto" unmountOnExit>
                  <ArgonBox p={2}>
                    {hasTransformations ? (
                      <ValueTransformationsTable
                        rules={orchestrator.valueTransformations}
                        fieldsToShow={fieldsConfig.valueTransformationFields}
                        customRender={{
                          valueTransformationGroupName: (item) => item.valueTransformationGroupName,
                        }}
                      />
                    ) : (
                      <ArgonTypography variant="caption" color="textSecondary">
                        No value transformations defined.
                      </ArgonTypography>
                    )}
                  </ArgonBox>
                </Collapse>
              </Card>
            );
          })}
        </Box>
      ))}
    </ArgonBox>
  );
}

export default DecompositionTab;
